import React, { useState, useEffect } from "react"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Textarea } from "../../ui/textarea"
import { TaskType, TaskStatus } from "./Constants/TaskConstants"
import MemberAssignmentPicker from "./MemberAssignmentPicker"

export default function TaskForm({ positions, tasks, actions = [], events = [], members = [], isForProfit = false, onSubmit, task, currentGroupId, loading, onClose }) {
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    description: "",
    type: TaskType.TASK,
    status: TaskStatus.TODO,
    positionId: "",
    reportsToPositionId: "",
    parentTaskId: "",
    groupId: currentGroupId,
    actionId: "",
    calendarEventId: "",
    assignedMemberId: "",
    isPaid: false,
    effort: "",
    dueDate: "",
  })

  // Initialize form with task data when editing
  useEffect(() => {
    if (task) {
      // Format the date for the input field
      const formattedDueDate = task.dueDate ? new Date(task.dueDate).toISOString().split("T")[0] : ""

      setFormData({
        ...task,
        dueDate: formattedDueDate,
        effort: task.effort || "",
        // Ensure these are strings for the select inputs
        positionId: task.positionId?.toString() || "",
        reportsToPositionId: task.reportsToPositionId?.toString() || "",
        parentTaskId: task.parentTaskId?.toString() || "",
        actionId: task.actionId?.toString() || "",
        calendarEventId: task.calendarEventId?.toString() || "",
        assignedMemberId: task.assignedMemberId || "",
        isPaid: Boolean(task.isPaid),
      })
    } else {
      // New task - set default values
      setFormData({
        id: "",
        title: "",
        description: "",
        type: TaskType.TASK,
        status: TaskStatus.TODO,
        positionId: positions.length > 0 ? positions[0].id.toString() : "",
        reportsToPositionId: "",
        parentTaskId: "",
        groupId: currentGroupId,
        actionId: "",
        calendarEventId: "",
        assignedMemberId: "",
        isPaid: false,
        effort: "",
        dueDate: "",
      })
    }
  }, [task, positions, currentGroupId])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSelectChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    // Prepare data for API
    const taskData = {
      ...formData,
      effort: formData.effort ? Number.parseInt(formData.effort, 10) : null,
      // Only include these if they have values
      reportsToPositionId: formData.reportsToPositionId || null,
      parentTaskId: formData.parentTaskId || null,
      actionId: formData.actionId ? Number(formData.actionId) : null,
      calendarEventId: formData.calendarEventId ? Number(formData.calendarEventId) : null,
      assignedMemberId: formData.assignedMemberId || null,
      isPaid: isForProfit && Boolean(formData.isPaid),
      dueDate: formData.dueDate || null,
      // Ensure groupId is set
      groupId: currentGroupId,
    }

    onSubmit(taskData)
  }

  // Handle the cancel button click
  const handleCancel = () => {
    // If onClose prop is provided, call it
    if (typeof onClose === "function") {
      onClose()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="group-task-form space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Title</label>
        <Input name="title" value={formData.title} onChange={handleChange} required />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <Textarea name="description" value={formData.description || ""} onChange={handleChange} rows={3} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Type</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleSelectChange}
            className="w-full rounded-md border border-gray-300 p-2"
          >
            {Object.values(TaskType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleSelectChange}
            className="w-full rounded-md border border-gray-300 p-2"
          >
            {Object.values(TaskStatus).map((status) => (
              <option key={status} value={status}>
                {status.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Position</label>
          <select
            name="positionId"
            value={formData.positionId}
            onChange={handleSelectChange}
            className="w-full rounded-md border border-gray-300 p-2"
          >
            {positions.map((position) => (
              <option key={position.id} value={position.id.toString()}>
                {position.title}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Reports To (Optional)</label>
          <select
            name="reportsToPositionId"
            value={formData.reportsToPositionId || ""}
            onChange={handleSelectChange}
            className="w-full rounded-md border border-gray-300 p-2"
          >
            <option value="">None</option>
            {positions.map((position) => (
              <option key={position.id} value={position.id.toString()}>
                {position.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Parent Task (Optional)</label>
          <select
            name="parentTaskId"
            value={formData.parentTaskId || ""}
            onChange={handleSelectChange}
            className="w-full rounded-md border border-gray-300 p-2"
          >
            <option value="">None</option>
            {tasks
              .filter((t) => t.id.toString() !== formData.id.toString()) // Don't allow self as parent
              .map((task) => (
                <option key={task.id} value={task.id.toString()}>
                  {task.title}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Effort Points (Optional)</label>
          <Input name="effort" type="number" min="0" value={formData.effort} onChange={handleChange} />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Due Date (Optional)</label>
        <Input name="dueDate" type="date" value={formData.dueDate || ""} onChange={handleChange} />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Assigned to</label>
        <MemberAssignmentPicker members={members} value={formData.assignedMemberId || null} onAssign={memberId => setFormData(current => ({ ...current, assignedMemberId: memberId || "" }))} disabled={loading} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div><label className="block text-sm font-medium mb-1">Action</label><select name="actionId" value={formData.actionId || ""} onChange={handleSelectChange} className="w-full rounded-md border border-gray-300 p-2"><option value="">No Action</option>{actions.map(action => <option key={action.id} value={action.id}>{action.title || action.actionStatement || `Action ${action.id}`}</option>)}</select></div>
        <div><label className="block text-sm font-medium mb-1">Calendar event</label><select name="calendarEventId" value={formData.calendarEventId || ""} onChange={handleSelectChange} className="w-full rounded-md border border-gray-300 p-2"><option value="">No calendar event</option>{events.filter(event => !formData.actionId || !event.actionId || String(event.actionId) === String(formData.actionId)).map(event => <option key={event.id} value={event.id}>{event.title}</option>)}</select></div>
      </div>
      {isForProfit && <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(formData.isPaid)} onChange={event => setFormData(current => ({ ...current, isPaid: event.target.checked }))} /> This is paid business work</label>}

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : task ? "Update Task" : "Create Task"}
        </Button>
      </div>
    </form>
  )
}

